import type {
    IPlugin,
    IProcessTelemetryContext,
    ITelemetryItem,
} from '@microsoft/1ds-analytics-js';
import { trace } from 'owa-trace';
import {
    DATA_POINT_EVENT_TYPES,
    CORE_ANALYTICS_SCHEMA_COLUMNS,
    EXTENDED_NETWORK_ANALYTICS_SCHEMA_COLUMNS,
    WATERFALL_COLUMNS,
    RUNTIME_ADDED_COLUMNS,
} from 'owa-analytics-types';

/**
 * Sentinel property set on `item.data` by the upstream pipeline for unified-
 * schema events (see OneDsWrapper.ts).  These events carry dynamic per-event
 * columns that cannot be enumerated in a static allowlist, so the column guard
 * is skipped while the table guard still applies.  The sentinel is always
 * deleted before the event reaches the POST channel.
 */
export const UNIFIED_SCHEMA_SENTINEL = '__owaUnifiedSchema';

/**
 * Sentinel property set on `item.data` by `logAddinsTelemetryEvent` for
 * telemetry originating from embedded components (Fluid/Loop, Feedback SDK,
 * add-ins).  These events use their own table names and column schemas that
 * are outside OWA's control, so both the table guard and column guard are
 * bypassed entirely.  The sentinel is stripped before the event reaches the
 * POST channel.
 */
export const ADDINS_TELEMETRY_SENTINEL = '__owaAddinsTelemetry';

/**
 * Tables (Aria tables) we write to.
 * Derived from the `DATA_POINT_EVENT_TYPES` const array in owa-analytics-types.
 * To add a new table, update that array — this set updates automatically.
 */
const ALLOWED_TABLES: Set<string> = new Set<string>(DATA_POINT_EVENT_TYPES);

/**
 * Top-level column names allowed in item.data.
 *
 * Built from four sources:
 * 1. CoreAnalyticsSchema columns (from owa-analytics-types)
 * 2. ExtendedNetworkAnalyticsSchema columns (from owa-analytics-types)
 * 3. Waterfall columns (WF_-prefixed, generated from PredefinedCheckpoint
 *    and CustomWaterfallRange source-of-truth arrays)
 * 4. Runtime-added columns (set by the analytics pipeline, from owa-analytics-types)
 */
const ALLOWED_COLUMNS: Set<string> = new Set<string>([
    ...CORE_ANALYTICS_SCHEMA_COLUMNS,
    ...EXTENDED_NETWORK_ANALYTICS_SCHEMA_COLUMNS,
    ...WATERFALL_COLUMNS,
    ...RUNTIME_ADDED_COLUMNS,
]);

/**
 * Truncates an attacker-controlled string before interpolating it into a
 * trace message.  Prevents log-injection payloads from inflating memory or
 * smuggling control characters into diagnostic output.
 */
function safeTruncate(value: string | undefined, maxLen = 100): string {
    if (!value) {
        return '<empty>';
    }
    const cleaned = value.replace(/[\x00-\x1f]/g, '');
    return cleaned.length > maxLen ? cleaned.slice(0, maxLen) + '\u2026' : cleaned;
}

/**
 * A 1DS telemetry plugin that enforces OWA's Aria schema at runtime.
 *
 * Two invariants are enforced before every event reaches the POST channel:
 *
 * 1. TABLE guard — `item.name` must be a known `DataPointEventType`.
 *    Events with unrecognised table names are dropped entirely, preventing
 *    new Aria tables from being created by typos or injection.
 *
 * 2. COLUMN guard — every key in `item.data` must be in the `ALLOWED_COLUMNS`
 *    set (which includes all schema columns, waterfall columns, and runtime-
 *    added columns).
 *    Unknown keys are stripped from the event before it is sent, preventing
 *    new columns from being created in existing Aria tables.
 *
 * Both violations are reported via `trace.warn` so they surface in dev/dogfood
 * and in error-report diagnostics without spamming production consumers.
 *
 * @remarks
 * Priority 500 places this plugin after PrivacyGuardPlugin (priority 499) and
 * well before the POST channel (priority 1011), ensuring all property-enrichment
 * plugins have run before schema validation occurs.
 */
export class SchemaEnforcementPlugin implements IPlugin {
    readonly identifier = 'OWASchemaEnforcementPlugin';
    // Run after PrivacyGuard (499), before the POST channel (1011).
    readonly priority = 500;

    // Required by IPlugin; nothing to initialise.
    initialize(
        _config: object,
        _core: object,
        _extensions: IPlugin[],
        _pluginChain?: object
    ): void {}

    processTelemetry(item: ITelemetryItem, itemCtx?: IProcessTelemetryContext): void {
        // ── 0. Addin bypass ─────────────────────────────────────────────────
        // Embedded components (Fluid/Loop, Feedback SDK, add-ins) own their
        // own telemetry contracts with table names and columns outside OWA's
        // schema.  Skip both guards and forward the event unchanged.
        if (item.data?.[ADDINS_TELEMETRY_SENTINEL]) {
            delete item.data[ADDINS_TELEMETRY_SENTINEL];
            itemCtx?.processNext(item);
            return;
        }

        // ── 1. Table guard ───────────────────────────────────────────────────
        if (!ALLOWED_TABLES.has(item.name)) {
            trace.warn(
                `[SchemaEnforcement] Dropping event with unrecognised table "${safeTruncate(
                    item.name
                )}". ` + `Add it to DATA_POINT_EVENT_TYPES in owa-analytics-types.`,
                'analytics'
            );
            // Do NOT call processNext — this drops the event from the pipeline.
            return;
        }

        // ── 2. Column guard ──────────────────────────────────────────────────
        // Unified-schema events carry dynamic per-event columns that cannot be
        // enumerated in a static allowlist.  The upstream pipeline marks them
        // with the sentinel so we skip column validation while still keeping
        // the table guard above.
        if (item.data) {
            const isUnifiedSchema = !!item.data[UNIFIED_SCHEMA_SENTINEL];
            if (isUnifiedSchema) {
                delete item.data[UNIFIED_SCHEMA_SENTINEL];
            } else {
                for (const key of Object.keys(item.data)) {
                    if (!ALLOWED_COLUMNS.has(key)) {
                        trace.warn(
                            `[SchemaEnforcement] Stripping unrecognised column "${safeTruncate(
                                key
                            )}" from table "${safeTruncate(item.name)}". ` +
                                `Add it to the schema arrays in owa-analytics-types.`,
                            'analytics'
                        );
                        delete item.data[key];
                    }
                }
            }
        }

        itemCtx?.processNext(item);
    }
}

// ── Exported for tests ───────────────────────────────────────────────────────
export { ALLOWED_TABLES, ALLOWED_COLUMNS };
